import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, ArrowRight, MapPin, Activity, Link2, Truck, Check } from "lucide-react";
import Reveal from "../components/Reveal";
import { ASSETS } from "../lib/brand";
import CTAStrip from "../components/CTAStrip";

const ACCENT = "#F8557D";

const SECTIONS = [
  { icon: MapPin, title: "Delivery Assignment", items: ["Automatic delivery allocation", "Delivery executive assignment", "Route planning"] },
  { icon: Activity, title: "Delivery Tracking", items: ["Order picked-up status", "Out-for-delivery tracking", "Delivered confirmation", "Failed & cancelled delivery management"] },
  { icon: Sparkles, title: "Real-Time Monitoring", items: ["Live delivery status updates", "Pending delivery reports", "Delivery performance monitoring"] },
  { icon: Link2, title: "Integration Benefits", items: ["Direct integration with Zen", "Direct integration with Primex", "Automatic sales sync", "Reduced delivery errors"] },
];

const BENEFITS = ["Faster order fulfillment", "Better customer experience", "Complete delivery visibility", "Improved operational efficiency"];
const IDEAL = ["Supermarkets", "Hypermarkets", "Grocery Stores", "Retail Chains", "Restaurants", "Pharmacy Delivery", "Home Delivery Businesses"];

export default function DigiposZento() {
  return (
    <div data-testid="digipos-zento-page">
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ background: `linear-gradient(135deg, #0B2545 0%, #7A3E00 50%, ${ACCENT} 100%)` }}>
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center text-white">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" />
                <Link to="/digipos" className="hover:text-white">DigiPOS</Link><ChevronRight className="h-3 w-3" />
                <span className="text-white">Zento</span>
              </div>
              <span className="gi-pill-dark"><Truck className="h-3.5 w-3.5" /> Delivery Management</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">DigiPOS Zento</h1>
              <p className="font-display text-xl mt-2" style={{ color: "#FFD7A8" }}>Smart Delivery Management Platform</p>
              <p className="text-white/75 mt-5 max-w-xl">
                Zento helps retailers and distributors efficiently manage home deliveries with real-time tracking and status updates. Fully integrated with both DigiPOS Zen and Primex.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="zento-demo-btn" className="gi-btn-primary">Request Demo <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/digipos" className="gi-btn-ghost">See Full Suite</Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative gi-float">
                <div className="absolute -inset-6 bg-gradient-to-br from-[#F77F00]/40 to-transparent blur-3xl rounded-full" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white border border-white/10">
                  <img src={ASSETS.zento} alt="DigiPOS Zento — Smart Delivery Management" className="w-full h-auto block" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest" style={{ color: ACCENT }}>Capabilities</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">From order to doorstep — tracked end-to-end</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 70}>
                <div className="gi-card gi-card-hover p-7" style={{ borderTop: `4px solid ${ACCENT}` }}>
                  <div className="h-12 w-12 rounded-xl grid place-items-center text-white mb-4" style={{ background: ACCENT }}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-3">{s.title}</h3>
                  <ul className="space-y-1.5 text-sm text-[#1A1A2E]/85">
                    {s.items.map((it) => <li key={it} className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: ACCENT }} />{it}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <Reveal>
            <div className="gi-card p-8 h-full" style={{ borderTop: `4px solid ${ACCENT}` }}>
              <h3 className="font-display font-bold text-2xl mb-6">Benefits</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BENEFITS.map((b) => (
                  <div key={b} className="flex items-start gap-3 p-4 rounded-xl bg-[#F5F8FF] border border-[#DDE6F0]">
                    <span className="h-7 w-7 rounded-md grid place-items-center shrink-0 text-white" style={{ background: ACCENT }}>
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium leading-snug">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="gi-card p-8 h-full" style={{ borderTop: `4px solid ${ACCENT}` }}>
              <h3 className="font-display font-bold text-2xl mb-6">Ideal For</h3>
              <div className="flex flex-wrap gap-2">
                {IDEAL.map((i) => (
                  <span key={i} className="px-4 py-2 rounded-full bg-[#F5F8FF] border border-[#DDE6F0] hover:border-[#F77F00] hover:text-[#F77F00] hover:bg-white transition text-sm font-medium">{i}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTAStrip heading="Faster deliveries. Happier customers." sub="Zento plugs into Zen or Primex in days, not weeks." />
    </div>
  );
}
